import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Fab, TextField, Tooltip } from '@mui/material';
import Toaster from '@shared/components/ui/Toaster';
import { captureScreenshot, isClientErrorReportingEnabled, reportClientError } from '@shared/telemetry';
import { getMessage } from '@shared/utils';
import { useState } from 'react';

const COMMENT_MAX_LENGTH = 1000;

interface ToastState {
  open: boolean;
  type: 'success' | 'error';
  message: string;
}

const closedToast: ToastState = { open: false, type: 'success', message: '' };

export const ReportProblemButton = () => {
  const [open, setOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [sending, setSending] = useState(false);
  const [comment, setComment] = useState('');
  const [screenshot, setScreenshot] = useState<Blob | null>(null);
  const [toast, setToast] = useState<ToastState>(closedToast);

  if (!isClientErrorReportingEnabled()) {
    return null;
  }

  const handleOpen = async () => {
    setCapturing(true);
    setScreenshot(await captureScreenshot());
    setCapturing(false);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setComment('');
    setScreenshot(null);
  };

  const handleSend = async () => {
    setSending(true);
    const id = await reportClientError(
      { source: 'manual', userComment: comment.trim() || undefined, message: 'Reporte manual del usuario' },
      { screenshot, force: true }
    );
    setSending(false);
    handleClose();
    setToast(
      id === null
        ? { open: true, type: 'error', message: getMessage('reportProblemFailed') }
        : { open: true, type: 'success', message: `${getMessage('reportProblemSent')}${id}` }
    );
  };

  return (
    <>
      <Tooltip title={getMessage('reportProblem')}>
        <Fab
          size="small"
          color="primary"
          aria-label={getMessage('reportProblem')}
          onClick={handleOpen}
          disabled={capturing}
          data-telemetry-ignore
          sx={{ position: 'fixed', right: 16, bottom: 16, zIndex: (theme) => theme.zIndex.speedDial }}>
          <BugReportOutlinedIcon />
        </Fab>
      </Tooltip>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" data-telemetry-ignore>
        <DialogTitle>{getMessage('reportProblemTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{getMessage('reportProblemDescription')}</DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label={getMessage('reportProblemComment')}
            value={comment}
            onChange={(event) => setComment(event.target.value.slice(0, COMMENT_MAX_LENGTH))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={sending}>
            {getMessage('reportProblemCancel')}
          </Button>
          <Button variant="contained" onClick={handleSend} disabled={sending}>
            {getMessage('reportProblemSend')}
          </Button>
        </DialogActions>
      </Dialog>
      <Toaster
        open={toast.open}
        type={toast.type}
        message={toast.message}
        handleClose={() => setToast(closedToast)}
      />
    </>
  );
};

export default ReportProblemButton;
